import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Loader2, ShieldCheck, Layers, ClipboardCheck, Activity, Gauge, Users2, RefreshCcw,
} from "lucide-react";

/**
 * Owner-only management drawer — edit tenant metadata, enable/disable,
 * re-seed the starter pack, and surface tenant stats + last-run schedulers.
 * Opens as a right-side panel; no router push so the Tenants page state is
 * preserved.
 */
export function ManageTenantDialog({ tenantId, onClose, onChanged }) {
  const [details, setDetails] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/tenants/${tenantId}/details`)
      .then(({ data }) => {
        if (cancelled) return;
        setDetails(data);
        setForm({
          name: data.tenant.name || "",
          region: data.tenant.region || "",
          locale: data.tenant.locale || "",
          sectors: (data.tenant.sectors || []).join(", "),
          active: data.tenant.active !== false,
        });
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load tenant"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [tenantId]);

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.patch(`/tenants/${tenantId}`, {
        name: form.name,
        region: form.region,
        locale: form.locale,
        sectors: form.sectors ? form.sectors.split(",").map((s) => s.trim()).filter(Boolean) : [],
        active: form.active,
      });
      toast.success(`${form.name} updated`);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const reseed = async () => {
    if (!window.confirm("Re-clone controls + KRIs + risk-appetite defaults into this tenant? Existing items are preserved.")) return;
    setReseeding(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/reseed-starter`);
      toast.success(`Starter pack reseeded: +${data.added.controls} controls, +${data.added.kris} KRIs`);
      const fresh = await api.get(`/tenants/${tenantId}/details`);
      setDetails(fresh.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reseed failed");
    } finally {
      setReseeding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40"
         onClick={onClose} data-testid="tenant-manage-drawer">
      <div className="relative h-full w-full max-w-xl overflow-y-auto bg-white p-6 shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="ek-eyebrow">Owner · Manage tenant</p>
            <h2 className="ek-heading mt-1 text-xl" data-testid="tenant-manage-title">
              {loading ? "Loading…" : details?.tenant?.name}
            </h2>
            {details?.tenant?.tenant_id && (
              <p className="mt-1 font-mono text-xs text-ek-ink/65">{details.tenant.tenant_id}</p>
            )}
          </div>
          <button onClick={onClose}
            className="rounded-full p-1 text-ek-ink/65 hover:bg-ek-canvas hover:text-ek-ink"
            data-testid="tenant-manage-close">✕</button>
        </div>

        {loading || !form ? (
          <p className="mt-6 text-sm text-ek-ink/65">Loading tenant details…</p>
        ) : (
          <>
            <section className="mt-6">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ek-ink/65">At a glance</h3>
              <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
                <Stat icon={<Users2 size={12} />} label="Users" value={`${details.stats.active_users}/${details.stats.users}`} testid={`tenant-stat-users-${tenantId}`} />
                <Stat icon={<ShieldCheck size={12} />} label="Risks" value={details.stats.risks} testid={`tenant-stat-risks-${tenantId}`} />
                <Stat icon={<Layers size={12} />} label="Suppliers" value={details.stats.suppliers} testid={`tenant-stat-suppliers-${tenantId}`} />
                <Stat icon={<ClipboardCheck size={12} />} label="Controls" value={details.stats.controls} testid={`tenant-stat-controls-${tenantId}`} />
                <Stat icon={<Activity size={12} />} label="KRIs" value={details.stats.kris} testid={`tenant-stat-kris-${tenantId}`} />
                <Stat icon={<Gauge size={12} />} label="Quality" value={details.stats.controls > 0 ? "Ready" : "Empty"} testid={`tenant-stat-quality-${tenantId}`} />
              </div>
            </section>

            <form onSubmit={save} className="mt-6 space-y-3" data-testid="tenant-manage-form">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ek-ink/65">Metadata</h3>
              <DrawerField label="Name" testid={`tenant-edit-name-${tenantId}`} value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} required />
              <div className="grid grid-cols-2 gap-3">
                <DrawerField label="Region" testid={`tenant-edit-region-${tenantId}`} value={form.region} onChange={(v) => setForm((f) => ({ ...f, region: v }))} />
                <DrawerField label="Locale" testid={`tenant-edit-locale-${tenantId}`} value={form.locale} onChange={(v) => setForm((f) => ({ ...f, locale: v }))} />
              </div>
              <DrawerField label="Sectors (comma-separated)" testid={`tenant-edit-sectors-${tenantId}`} value={form.sectors} onChange={(v) => setForm((f) => ({ ...f, sectors: v }))} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active}
                  disabled={tenantId === "tnt_default"}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  data-testid={`tenant-edit-active-${tenantId}`} />
                <span className={tenantId === "tnt_default" ? "text-ek-ink/45" : "text-ek-ink/80"}>
                  {tenantId === "tnt_default"
                    ? "Active — the default tenant cannot be disabled (safety guard)."
                    : "Active — disabling blocks all member logins immediately."}
                </span>
              </label>
              <div className="flex items-center gap-2 pt-2">
                <button type="submit" disabled={busy}
                  className="ek-btn flex items-center gap-1.5"
                  data-testid={`tenant-edit-save-${tenantId}`}>
                  {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save changes
                </button>
                <button type="button" onClick={reseed} disabled={reseeding}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-medium text-ek-ink hover:bg-ek-purple/5 disabled:opacity-60"
                  data-testid={`tenant-edit-reseed-${tenantId}`}
                  title="Re-clone controls + KRIs + appetite defaults (idempotent — skips existing items)">
                  {reseeding ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />} Re-seed starter pack
                </button>
              </div>
            </form>

            <section className="mt-6">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ek-ink/65">Scheduled jobs · last run</h3>
              <ul className="mt-2 space-y-1 text-xs text-ek-ink/80">
                <li><span className="text-ek-ink/55">Trend snapshot</span> · {fmtTs(details.schedulers.trend_snapshot?.ts)}</li>
                <li><span className="text-ek-ink/55">Quality snapshot</span> · {fmtTs(details.schedulers.quality_snapshot?.ts)} {details.schedulers.quality_snapshot?.synthetic ? "(synthetic)" : ""}</li>
                <li><span className="text-ek-ink/55">Risk velocity snapshot</span> · {fmtTs(details.schedulers.risk_velocity?.ts)}</li>
              </ul>
            </section>

            {details.recent_leads?.length > 0 && (
              <section className="mt-6">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-ek-ink/65">Recent leads attributable</h3>
                <ul className="mt-2 space-y-1 text-xs text-ek-ink/80">
                  {details.recent_leads.map((l) => (
                    <li key={l.lead_id} className="flex items-center justify-between gap-3 border-b border-black/5 pb-1">
                      <span>{l.email}</span>
                      <span className="text-ek-ink/55">{fmtTs(l.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon, label, value, testid }) => (
  <div className="rounded-lg border border-black/5 bg-ek-canvas/40 p-2" data-testid={testid}>
    <p className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wide text-ek-ink/65">{icon} {label}</p>
    <p className="mt-1 font-display text-base font-bold text-ek-ink">{value}</p>
  </div>
);

const DrawerField = ({ label, value, onChange, placeholder, type = "text", required, testid, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-medium text-ek-ink/60">{label}</span>
    <input
      type={type}
      value={value}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 text-sm"
    />
  </label>
);

const fmtTs = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const ms = Date.now() - d.getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days}d ago`;
  const hrs = Math.floor(ms / 3_600_000);
  if (hrs >= 1) return `${hrs}h ago`;
  const mins = Math.floor(ms / 60_000);
  return mins <= 0 ? "just now" : `${mins}m ago`;
};
